// @ts-strict-ignore
import useForm from 'antd/lib/form/hooks/useForm';
import { useDispatch, useSelector } from 'react-redux';
import { getUpdated, getIsLoading, getError, appleReleaseTypeForReleaseMethod } from '../helpers';
import { RootState } from '../reducers/reducers';
import {
    AppleAppStoreData,
    AppleAppStoreDefaultSettings,
    AppleAppStoreReleaseType,
    RunwayResponseType,
} from '../types';
import * as React from 'react';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import Select from 'antd/lib/select';
import { allCases, Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { displayVersionReleaseTypeSetting } from '../helpers/displayHelpers';
import DatePicker from 'antd/lib/date-picker';
import { tryUpdateRelease, UpdateAppleAppStoreSettingsRequestForReleaseStep } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { dayjs } from '@/extendedDayjs';
import { globalMessage } from '../utils/globalMessage';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { ReleaseOnlySettingsAlert } from './sharedComponents/shared';

enum PhasedReleaseOptions {
    PhasedReleaseEnabled,
    PhasedReleaseDisabled,
}

interface EditAppleAppStoreReleaseSettingsModalProps extends ReleaseComponentProps {
    displayableVersion: string;
    open: boolean;
    appStoreReleaseSettings?: AppleAppStoreDefaultSettings;
    appStoreData?: AppleAppStoreData;
    isRollback: boolean;
    onClose: () => void;
    onSuccess: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const EditAppleAppStoreReleaseSettingsModal: React.FC<EditAppleAppStoreReleaseSettingsModalProps> = (
    props: EditAppleAppStoreReleaseSettingsModalProps,
) => {
    const [form] = useForm();
    const [versionReleaseType, setVersionReleaseType] = React.useState(null);
    const dispatch = useDispatch();

    const [
        updateAppStoreSettingsStepRun,
        updateAppStoreSettingsIsLoadingStepRun,
        updateAppStoreSettingsResponseTypeStepRun,
        updateAppStoreSettingsErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateAppStoreSettings, props.stepRunComponentProps);

    const releaseSettingsUpdatedSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.UpdateAppleAppStoreSettings, state, props.releaseId),
    );
    const releaseSettingsUpdateLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.UpdateAppleAppStoreSettings, state, props.releaseId),
    );

    const releaseSettingsUpdateErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.UpdateAppleAppStoreSettings, state, props.releaseId),
    );

    const releaseSettingsUpdatedSuccess =
        props.stepRunComponentProps ?
            updateAppStoreSettingsResponseTypeStepRun === RunwayResponseType.Success
        :   releaseSettingsUpdatedSuccessReleaseStep;
    const releaseSettingsUpdateLoading =
        props.stepRunComponentProps ? updateAppStoreSettingsIsLoadingStepRun : releaseSettingsUpdateLoadingReleaseStep;

    const releaseSettingsUpdateError =
        props.stepRunComponentProps ? updateAppStoreSettingsErrorStepRun : releaseSettingsUpdateErrorReleaseStep;

    const onSaveChanges = () => {
        form.validateFields().then((values) => {
            const earliestReleaseDate =
                values.earliestReleaseDate ? values.earliestReleaseDate.startOf('hour').toISOString() : null;

            const releaseType = values.releaseType;
            const phasedReleaseEnabled = values.isPhasedReleaseEnabled === PhasedReleaseOptions.PhasedReleaseEnabled;

            const request: UpdateAppleAppStoreSettingsRequestForReleaseStep = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                releaseType,
                earliestReleaseDate,
                phasedReleaseEnabled,
            };

            if (props.stepRunComponentProps) {
                updateAppStoreSettingsStepRun({
                    releaseType: releaseType,
                    earliestReleaseDate: earliestReleaseDate,
                    phasedReleaseEnabled: phasedReleaseEnabled,
                });
            } else {
                dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateAppleAppStoreSettings, request, props.releaseId));
            }
        });
    };

    const title = `Edit App Store release settings for ${props.displayableVersion}`;

    React.useEffect(() => {
        if (releaseSettingsUpdatedSuccess) {
            globalMessage.success('Release settings updated!');

            props.onSuccess();
        }
    }, [releaseSettingsUpdatedSuccess]);

    React.useEffect(() => {
        if (releaseSettingsUpdateError) {
            globalMessage.error(releaseSettingsUpdateError);
        }
    }, [releaseSettingsUpdateError]);

    React.useEffect(() => {
        if (props.open) {
            if (props.appStoreData) {
                form.setFieldsValue({
                    releaseType: props.appStoreData.versionReviewSubmissionItem.releaseType,
                    earliestReleaseDate:
                        props.appStoreData?.versionReviewSubmissionItem.earliestReleaseDate ?
                            dayjs(props.appStoreData.versionReviewSubmissionItem.earliestReleaseDate)
                        :   null,
                    isPhasedReleaseEnabled:
                        props.appStoreData.phasedRelease ?
                            PhasedReleaseOptions.PhasedReleaseEnabled
                        :   PhasedReleaseOptions.PhasedReleaseDisabled,
                });

                setVersionReleaseType(props.appStoreData.versionReviewSubmissionItem.releaseType);
            } else if (props.appStoreReleaseSettings) {
                const releaseMethod = (props.appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod;
                const isPhasedRelease = (props.appStoreReleaseSettings as AppleAppStoreDefaultSettings).phasedRelease;

                form.setFieldsValue({
                    releaseType: appleReleaseTypeForReleaseMethod(releaseMethod),
                    earliestReleaseDate: null,
                    isPhasedReleaseEnabled:
                        isPhasedRelease ?
                            PhasedReleaseOptions.PhasedReleaseEnabled
                        :   PhasedReleaseOptions.PhasedReleaseDisabled,
                });

                setVersionReleaseType(AppleAppStoreReleaseType.Manual);
            }
        }
    }, [props.open]);

    const todayDate = dayjs();

    return (
        <Modal
            title={title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: releaseSettingsUpdateLoading }}
            onCancel={props.onClose}
            destroyOnClose
            open={props.open}
            okText="Update App Store release settings"
            cancelText="Cancel"
            afterClose={() => form.resetFields()}
        >
            <ReleaseOnlySettingsAlert
                displayableVersion={props.displayableVersion}
                isWorkflowStep={!!props.stepRunComponentProps}
                onCloseModal={props.onClose}
            />
            <Form form={form} layout="vertical" onFinish={onSaveChanges}>
                <FormItem label="Version release" name="releaseType" rules={[{ required: true }]}>
                    <Select onChange={(value) => setVersionReleaseType(value)}>
                        {allCases(AppleAppStoreReleaseType).map((releaseType, index) => {
                            return (
                                <Select.Option key={index} value={releaseType}>
                                    {displayVersionReleaseTypeSetting(releaseType)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </FormItem>
                {versionReleaseType === AppleAppStoreReleaseType.Scheduled && (
                    <FormItem label="Earliest release date" name="earliestReleaseDate" rules={[{ required: true }]}>
                        <DatePicker
                            needConfirm={false}
                            showTime={{
                                showNow: false,
                                format: 'MM/DD [at] h:00 A',
                            }}
                            disabledDate={(date) => {
                                return date.isBefore(todayDate, 'day');
                            }}
                            format={'MM/DD [at] h:00 A'}
                            style={{ width: '50%', minWidth: 200, maxWidth: 300 }}
                        />
                    </FormItem>
                )}
                <FormItem
                    label={
                        <Space>
                            <Text>Phased release</Text>
                            <TooltipInfoIcon
                                tooltipContent={
                                    <>
                                        Releases this update in stages over a 7-day period to users who have turned on
                                        automatic updates. (1% of users on Day 1, 2% on Day 2, 5% on Day 3, 10% on Day
                                        4, 20% on Day 5, 50% on Day 6, and 100% on Day 7).
                                        <br />
                                        <br />
                                        This version will still be available to all users as a manual update from the
                                        App Store.
                                        <br />
                                        <br />
                                        You can pause the phased release for up to 30 days or release this update to all
                                        users at any time.
                                    </>
                                }
                            />
                        </Space>
                    }
                    name="isPhasedReleaseEnabled"
                    rules={[{ required: true }]}
                >
                    <Select>
                        <Select.Option value={PhasedReleaseOptions.PhasedReleaseEnabled}>
                            Release update over 7-day period using phased release
                        </Select.Option>
                        <Select.Option value={PhasedReleaseOptions.PhasedReleaseDisabled}>
                            Release update to all users immediately
                        </Select.Option>
                    </Select>
                </FormItem>
            </Form>
        </Modal>
    );
};

export default EditAppleAppStoreReleaseSettingsModal;
